import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { TrendingUp } from "lucide-react";

export default function History() {
  const navigate = useNavigate();
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    api.get("/analytics/history").then((r) => setTrend(r.data.trend || []));
  }, []);

  const chartData = trend.map((t, i) => ({
    idx: `#${i + 1}`,
    date: new Date(t.date).toLocaleDateString(),
    accuracy: t.accuracy,
    speed: t.speed,
    decision: t.decision_quality,
    pressure: t.pressure_index,
    pct: t.max_score ? Math.round((t.score / t.max_score) * 100) : 0,
  }));

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-8 fade-up">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Progress</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Performance history</h1>
          <p className="text-stone-500 mt-2">Every metric tracked across every attempt.</p>
        </div>

        {trend.length === 0 ? (
          <Card className="p-16 rounded-md border-stone-200 shadow-none text-center">
            <TrendingUp className="h-12 w-12 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500">Complete a graded exam to see your history.</p>
          </Card>
        ) : (
          <>
            <Card className="p-6 rounded-md border-stone-200 shadow-none">
              <h3 className="font-heading text-xl font-semibold mb-4">Score % over time</h3>
              <div className="h-72">
                <ResponsiveContainer>
                  <LineChart data={chartData}>
                    <CartesianGrid stroke="#e7e5e4" strokeOpacity={0.5} vertical={false} />
                    <XAxis dataKey="idx" tick={{ fill: "#78716c", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} />
                    <YAxis tick={{ fill: "#78716c", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} unit="%" />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e7e5e4", borderRadius: 6, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                    <Line type="monotone" dataKey="pct" stroke="#ea580c" strokeWidth={2.5} dot={{ fill: "#ea580c", r: 4 }} name="Score %" />
                    <Line type="monotone" dataKey="accuracy" stroke="#059669" strokeWidth={2} dot={{ fill: "#059669", r: 3 }} name="Accuracy" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-6 rounded-md border-stone-200 shadow-none">
              <h3 className="font-heading text-xl font-semibold mb-4">Metrics trend</h3>
              <div className="h-72">
                <ResponsiveContainer>
                  <LineChart data={chartData}>
                    <CartesianGrid stroke="#e7e5e4" strokeOpacity={0.5} vertical={false} />
                    <XAxis dataKey="idx" tick={{ fill: "#78716c", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} />
                    <YAxis tick={{ fill: "#78716c", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e7e5e4", borderRadius: 6, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                    <Line type="monotone" dataKey="speed" stroke="#ea580c" strokeWidth={2} name="Speed" />
                    <Line type="monotone" dataKey="decision" stroke="#059669" strokeWidth={2} name="Decision Quality" />
                    <Line type="monotone" dataKey="pressure" stroke="#e11d48" strokeWidth={2} name="Pressure Index" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-6 rounded-md border-stone-200 shadow-none">
              <h3 className="font-heading text-xl font-semibold mb-4">Attempts log</h3>
              <div className="space-y-2">
                {trend.map((t, i) => (
                  <div
                    key={t.attempt_id}
                    className="flex items-center justify-between p-3 border border-stone-200 rounded-md hover:border-orange-400 cursor-pointer transition-colors"
                    onClick={() => navigate(`/results/${t.attempt_id}`)}
                    data-testid={`history-row-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{t.exam_title}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500 mt-1">
                        {new Date(t.date).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex gap-8 items-center font-mono text-sm">
                      <div className="text-right">
                        <div className="font-semibold text-orange-600 tabular-nums">{t.score}/{t.max_score}</div>
                        <div className="text-[10px] text-stone-500">score</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-emerald-600 tabular-nums">{t.accuracy}%</div>
                        <div className="text-[10px] text-stone-500">accuracy</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </AppLayout>
  );
}
